// src/components/calendar/edit/OvertimeModal.js
import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  IconButton,
  CircularProgress,
  LinearProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSnackbar } from "notistack";
import { CalendarContext } from "../../context/CalendarContext";
import {
  OVERTIME_TYPE_LABELS,
  useHandleAddOvertime,
  useHandleUpdateOvertime,
  useHandleRemoveOvertime,
  fetchOvertimeForDay,
} from "./Overtime";

const formatHour = (value) => (value ? value.slice(0, 5) : "—");

const emptyForm = { type: "extra", hour_from: "", hour_to: "", reason: "" };

const OvertimeModal = ({ open, onClose, ci, station_id, day, employeeName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setOvertimeForDay } = useContext(CalendarContext);
  const addOvertime = useHandleAddOvertime();
  const updateOvertime = useHandleUpdateOvertime();
  const removeOvertime = useHandleRemoveOvertime();

  const [existing, setExisting] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [formValues, setFormValues] = useState(emptyForm);

  const isEditing = editingId !== null;

  const loadExisting = useCallback(async () => {
    if (!ci || !day) return;
    setLoadingExisting(true);
    const items = await fetchOvertimeForDay({ ci, day });
    setExisting(items);
    setOvertimeForDay?.(ci, day, items);
    setLoadingExisting(false);
  }, [ci, day, setOvertimeForDay]);

  useEffect(() => {
    if (open) {
      setErrorMessage("");
      setEditingId(null);
      setFormValues(emptyForm);
      loadExisting();
    }
  }, [open, loadExisting]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setFormValues({
      type: item.type,
      hour_from: formatHour(item.hour_from),
      hour_to: formatHour(item.hour_to),
      reason: item.reason || "",
    });
    setErrorMessage("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormValues(emptyForm);
    setErrorMessage("");
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!formValues.hour_from || !formValues.hour_to || !formValues.type) {
      enqueueSnackbar("Completa tipo, hora de inicio y hora de fin", { variant: "warning" });
      return;
    }
    if (formValues.hour_to <= formValues.hour_from) {
      enqueueSnackbar("La hora de fin debe ser posterior a la hora de inicio", { variant: "warning" });
      return;
    }
    if (!formValues.reason?.trim()) {
      enqueueSnackbar("El motivo es obligatorio", { variant: "warning" });
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    const payload = {
      hour_from: formValues.hour_from,
      hour_to: formValues.hour_to,
      type: formValues.type,
      reason: formValues.reason?.trim() || undefined,
    };

    const result = isEditing
      ? await updateOvertime({ id: editingId, station_id, ...payload })
      : await addOvertime({ ci, day, station_id, ...payload });

    setIsSaving(false);

    if (result.ok) {
      enqueueSnackbar(
        `${OVERTIME_TYPE_LABELS[formValues.type]} ${isEditing ? "actualizada" : "registrada"} correctamente.`,
        { variant: "success" }
      );
      setEditingId(null);
      setFormValues(emptyForm);
      loadExisting();
    } else {
      const message =
        result.info?.message ||
        `No se pudo ${isEditing ? "actualizar" : "registrar"} la hora extra.`;
      setErrorMessage(message);
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleDelete = async (id) => {
    setRemovingId(id);
    const result = await removeOvertime({ id, station_id });
    setRemovingId(null);
    if (result.ok) {
      enqueueSnackbar("Registro eliminado.", { variant: "success" });
      if (editingId === id) handleCancelEdit();
      loadExisting();
    } else {
      enqueueSnackbar(result.info?.message || "No se pudo eliminar el registro.", { variant: "error" });
    }
  };

  return (
    <Modal open={open} onClose={isSaving ? undefined : onClose}>
      <Box sx={{ position: "relative", width: 440, mx: "auto", mt: "8%" }}>
        {isSaving && (
          <LinearProgress sx={{ position: "absolute", top: 0, left: 0, width: "100%" }} />
        )}

        <Box sx={{ p: 3, backgroundColor: "white", borderRadius: 1, boxShadow: 24, maxHeight: "80vh", overflowY: "auto" }}>
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={isSaving ? undefined : onClose}
            disabled={isSaving}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" align="center" gutterBottom>
            {isEditing ? "Editar hora extra" : "Agregar hora extra"}
          </Typography>
          <Typography variant="body2" align="center" sx={{ fontWeight: 600 }}>
            {employeeName || "Empleado"}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
            {day}
          </Typography>

          {errorMessage && !isSaving && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            select
            margin="normal"
            label="Tipo"
            name="type"
            value={formValues.type}
            onChange={handleChange}
          >
            <MenuItem value="extra">{OVERTIME_TYPE_LABELS.extra}</MenuItem>
            <MenuItem value="recuperacion">{OVERTIME_TYPE_LABELS.recuperacion}</MenuItem>
          </TextField>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Hora inicio"
              name="hour_from"
              type="time"
              value={formValues.hour_from}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Hora fin"
              name="hour_to"
              type="time"
              value={formValues.hour_to}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <TextField
            fullWidth
            required
            margin="normal"
            label="Motivo"
            name="reason"
            value={formValues.reason}
            onChange={handleChange}
            multiline
            minRows={2}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
            {isEditing && (
              <Button onClick={handleCancelEdit} disabled={isSaving}>
                Cancelar edición
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {isSaving ? "Guardando..." : isEditing ? "Guardar cambios" : "Guardar"}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Registradas este día
          </Typography>
          {loadingExisting ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : existing.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay horas extra registradas este día.
            </Typography>
          ) : (
            <List dense>
              {existing.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{ pl: 0, bgcolor: editingId === item.id ? "action.selected" : undefined, borderRadius: 1 }}
                >
                  <ListItemText
                    primary={`${OVERTIME_TYPE_LABELS[item.type] || item.type} — ${formatHour(item.hour_from)} a ${formatHour(item.hour_to)}`}
                    secondary={item.reason || undefined}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleStartEdit(item)}
                      disabled={removingId === item.id || (isEditing && editingId !== item.id)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDelete(item.id)}
                      disabled={removingId === item.id}
                    >
                      {removingId === item.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default OvertimeModal;
